import { useState, useEffect, useRef } from 'react';
import { Menu, X, Mail, Phone } from 'lucide-react';
import { FOUNDING_MEMBER_PRICE } from '../constants';

interface NavbarProps {
  onOpenSignup: () => void;
}

export default function Navbar({ onOpenSignup }: NavbarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showAthnxGym, setShowAthnxGym] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const contactRef = useRef<HTMLDivElement>(null);

  // Close contact popup on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (contactRef.current && !contactRef.current.contains(e.target as Node)) {
        setContactOpen(false);
      }
    }
    if (contactOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [contactOpen]);

  useEffect(() => {
    const interval = setInterval(() => {
      setShowAthnxGym(prev => !prev);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  return (
    <nav className="fixed top-0 left-0 right-0 z-40 bg-[#161b26]/80 backdrop-blur-lg border-b border-white/5">
      <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
        {/* Rotating brand logo */}
        <a href="/" className="flex items-center h-8 overflow-hidden relative" style={{ minWidth: '140px' }}>
          {/* Athenex Solutions logo */}
          <img
            src="/images/athenex-logo.svg"
            alt="Athenex Solutions"
            className={`h-7 w-auto dark:invert absolute transition-all duration-500 ${
              showAthnxGym ? 'opacity-0 -translate-y-4' : 'opacity-100 translate-y-0'
            }`}
          />
          {/* AthnxGym text */}
          <span
            className={`font-logo-main text-xl text-white absolute transition-all duration-500 ${
              showAthnxGym ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            Athnx<span className="text-blue-400">Gym</span>
          </span>
        </a>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-6">
          <a href="#features" className="text-sm text-white/50 hover:text-white transition-colors">Features</a>
          <a href="#pricing" className="text-sm text-white/50 hover:text-white transition-colors">Pricing</a>
          <div className="relative" ref={contactRef}>
            <button onClick={() => setContactOpen(!contactOpen)} className="text-sm text-white/50 hover:text-white transition-colors">Contact</button>
            {contactOpen && (
              <div className="absolute top-full right-0 mt-2 w-64 rounded-xl border border-white/10 bg-[#2d313c] shadow-xl p-4 space-y-3">
                <div className="flex items-center gap-2.5">
                  <Mail className="w-4 h-4 text-blue-400 flex-shrink-0" />
                  <span className="text-white/80 text-sm">support@athenex.solutions</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <Phone className="w-4 h-4 text-blue-400 flex-shrink-0" />
                  <span className="text-white/80 text-sm">949-616-6891</span>
                </div>
              </div>
            )}
          </div>
          <a href="/portal/login" className="text-sm text-white/50 hover:text-white transition-colors">Login</a>
          <button
            onClick={onOpenSignup}
            className="px-4 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-bold rounded-lg transition-colors"
          >
            Join — ${FOUNDING_MEMBER_PRICE}/mo
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden text-white/60 hover:text-white"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-white/5 bg-[#161b26]/95 backdrop-blur-lg px-4 py-4 space-y-3">
          <a href="#features" onClick={() => setMobileOpen(false)} className="block text-white/70 hover:text-white text-sm">Features</a>
          <a href="#pricing" onClick={() => setMobileOpen(false)} className="block text-white/70 hover:text-white text-sm">Pricing</a>
          <button onClick={() => setContactOpen(!contactOpen)} className="block text-white/70 hover:text-white text-sm text-left">Contact</button>
          {contactOpen && (
            <div className="ml-2 pl-3 border-l border-white/10 space-y-2">
              <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-blue-400" /><span className="text-white/70 text-sm">support@athenex.solutions</span></div>
              <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-blue-400" /><span className="text-white/70 text-sm">949-616-6891</span></div>
            </div>
          )}
          <button
            onClick={() => { onOpenSignup(); setMobileOpen(false); }}
            className="w-full py-2.5 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-lg transition-colors text-sm"
          >
            Become a Founding Member
          </button>
        </div>
      )}
    </nav>
  );
}
